/**
 * CSV export for Job CRM contacts.
 *
 * Two layouts, both triggered from `ContactsView`:
 *
 * 1. Table CSV — matches the on-screen contacts table (Name, Type, Roles,
 *    contact details, split exposure). Used by "Current view" / "All".
 *    Money is written as a real number (or blank) so the practitioner can
 *    sort / sum in Excel.
 *
 * 2. IPS import format (MOR-441) — creditor-shaped columns for IPS. For
 *    secured creditors we also project linked `securityInterestEntries`
 *    (PPSR registration + collateral) into the IPS columns.
 *
 * Operates on the in-memory rows already loaded for the table.
 */

import type { Id } from "@cvx/_generated/dataModel";
import type { CustomExportDataset } from "@/components/customExport";
import {
  CONTACT_TYPE_META,
  ROLE_META,
  distinctRoles,
  formatAbn,
  formatAcn,
  splitExposure,
  type JobContactRow,
} from "./types";

const TABLE_HEADERS = [
  "Name",
  "ACN",
  "ABN",
  "Group",
  "Type",
  "Roles",
  "Email",
  "Phone",
  "Address",
  "Owed to",
  "Owed by",
  "Other exposure",
  "Status",
  "Linked firm contact",
] as const;

const IPS_HEADERS = [
  "No",
  "Type of creditor",
  "Creditor Name or Company Name",
  "Address Line 1",
  "Address Line 2",
  "Suburb",
  "State",
  "Postcode",
  "Email Address",
  "If secured enter Yes",
  "PPSR if applicable",
  "If secured give asset details",
  "Amount owing",
  "If related party enter Yes",
  "Phone",
] as const;

const SECURED_ROLES: ReadonlySet<string> = new Set(["secured_creditor"]);
const RELATED_ROLES: ReadonlySet<string> = new Set([
  "related_entity",
  "related_individual",
]);

const STATUS_LABEL: Record<JobContactRow["status"], string> = {
  active: "Active",
  archived: "Archived",
  merged: "Merged",
  deleted: "Deleted",
};

// Shape of a single security_interest row inside the per-contact source
// rows map. Mirrors the fields the IPS exporter consumes from
// `securityInterestEntries`; broader columns exist on the table but are
// not part of the IPS export.
interface SecurityInterestSourceRow {
  ppsrRegistrationNumber?: string;
  collateralDescription?: string;
  collateralType?: string;
  collateralClass?: string;
}

interface SourceRowEntry {
  link: { kind: string };
  row: unknown;
}

interface ContactSourceRowGroups {
  security_interest?: Array<SourceRowEntry>;
  [key: string]: Array<SourceRowEntry> | undefined;
}

export type SourceRowsByContact = Record<
  Id<"jobContacts">,
  ContactSourceRowGroups
>;

export type ContactsCsvScope = "current" | "all" | "selected";

function escapeCsvCell(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return "";
  const s = String(value);
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function joinUnique(values: Array<string | undefined | null>): string {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const v of values) {
    const trimmed = (v ?? "").trim();
    if (!trimmed || seen.has(trimmed)) continue;
    seen.add(trimmed);
    out.push(trimmed);
  }
  return out.join("; ");
}

function money(value: number): number | "" {
  return value > 0 ? value : "";
}

function formatAddress(contact: JobContactRow): string {
  if (contact.addresses.length === 0) return "";
  return contact.addresses
    .map((address) =>
      [
        address.line1,
        address.line2,
        address.suburb,
        address.state,
        address.postcode,
      ]
        .filter(Boolean)
        .join(", "),
    )
    .filter(Boolean)
    .join(" | ");
}

function rowToTableRecord(contact: JobContactRow): Array<string | number> {
  const roles = distinctRoles(contact);
  const { creditor, debtor, unclassified } = splitExposure(contact);
  return [
    contact.displayName,
    contact.acn ? formatAcn(contact.acn) : "",
    contact.abn ? formatAbn(contact.abn) : "",
    contact.groupName ?? "",
    CONTACT_TYPE_META[contact.contactType].label,
    roles.map((role) => ROLE_META[role].label).join("; "),
    contact.effectiveEmail ?? "",
    contact.effectivePhone ?? "",
    formatAddress(contact),
    money(creditor),
    money(debtor),
    money(unclassified),
    STATUS_LABEL[contact.status],
    contact.firmContact?.displayName ?? "",
  ];
}

function rowToIpsRecord(
  contact: JobContactRow,
  index: number,
  sourceRows: SourceRowsByContact | null,
): Array<string | number> {
  const roles = distinctRoles(contact);
  const isSecured = roles.some((r) => SECURED_ROLES.has(r));
  const isRelated = roles.some((r) => RELATED_ROLES.has(r));
  const { creditor } = splitExposure(contact);
  const address = contact.addresses[0];

  const typeOfCreditor = roles
    .filter((r) => r === "secured_creditor" || r === "unsecured_creditor")
    .map((r) => (r === "secured_creditor" ? "Secured" : "Unsecured"))
    .join(", ");

  // Security-interest enrichment — only meaningful for secured creditors,
  // but we surface whatever rows are linked regardless so the export is
  // forgiving of misclassified roles.
  const siRows = (sourceRows?.[contact._id]?.security_interest ?? [])
    .map((entry) => entry.row as SecurityInterestSourceRow)
    .filter(Boolean);
  const ppsrNumbers = joinUnique(siRows.map((r) => r.ppsrRegistrationNumber));
  const collateralDetails = joinUnique(
    siRows.map((r) => {
      const desc = (r.collateralDescription ?? "").trim();
      const cls = (r.collateralClass ?? "").trim();
      if (desc && cls) return `${cls} — ${desc}`;
      return desc || cls;
    }),
  );

  return [
    index + 1,
    typeOfCreditor,
    contact.displayName,
    address?.line1 ?? "",
    address?.line2 ?? "",
    address?.suburb ?? "",
    address?.state ?? "",
    address?.postcode ?? "",
    contact.effectiveEmail ?? "",
    isSecured ? "Yes" : "",
    ppsrNumbers,
    collateralDetails,
    creditor > 0 ? creditor.toFixed(2) : "",
    isRelated ? "Yes" : "",
    contact.effectivePhone ?? "",
  ];
}

function buildCsv(
  headers: ReadonlyArray<string>,
  records: Array<Array<string | number>>,
): string {
  const lines: string[] = [headers.map(escapeCsvCell).join(",")];
  for (const record of records) {
    lines.push(record.map(escapeCsvCell).join(","));
  }
  return lines.join("\r\n");
}

function timestampSlug(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}` +
    `-${pad(d.getHours())}${pad(d.getMinutes())}`
  );
}

function downloadCsv(csvBody: string, filename: string): void {
  // Excel-friendly CSV: UTF-8 BOM so non-ASCII names render correctly when
  // double-clicked into Excel.
  const csv = "\uFEFF" + csvBody;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function scopeSlug(scope: ContactsCsvScope): string {
  switch (scope) {
    case "current":
      return "current-view";
    case "selected":
      return "selected";
    case "all":
      return "all";
    default: {
      const _exhaustive: never = scope;
      return _exhaustive;
    }
  }
}

/**
 * Download a table-shaped CSV that mirrors the on-screen contacts columns.
 */
export function exportContactsTableToCsv(
  rows: Array<JobContactRow>,
  scope: ContactsCsvScope,
): void {
  if (rows.length === 0) {
    throw new Error("No contacts to export.");
  }
  downloadCsv(
    buildCsv(
      TABLE_HEADERS,
      rows.map((row) => rowToTableRecord(row)),
    ),
    `contacts-${scopeSlug(scope)}-${timestampSlug()}.csv`,
  );
}

/**
 * Datasets for the custom export tool (MOR-1048): the same two layouts the
 * CSV menu offers, as composable tables the practitioner can filter, pivot,
 * save and export to Excel.
 */
export function buildContactsCustomExportDatasets(
  rows: Array<JobContactRow>,
  sourceRows: SourceRowsByContact | null,
): CustomExportDataset[] {
  return [
    {
      key: "job-contacts",
      label: "Contacts",
      headers: [...TABLE_HEADERS],
      rows: rows.map((row) => rowToTableRecord(row)),
    },
    {
      key: "job-contacts-ips",
      label: "IPS import format",
      headers: [...IPS_HEADERS],
      rows: rows.map((row, i) => rowToIpsRecord(row, i, sourceRows)),
    },
  ];
}

/**
 * Download the IPS creditor-import CSV (MOR-441).
 */
export function exportContactsIpsToCsv(
  rows: Array<JobContactRow>,
  sourceRows: SourceRowsByContact | null,
  scope: ContactsCsvScope,
): void {
  if (rows.length === 0) {
    throw new Error("No contacts to export.");
  }
  downloadCsv(
    buildCsv(
      IPS_HEADERS,
      rows.map((row, i) => rowToIpsRecord(row, i, sourceRows)),
    ),
    `contacts-ips-${scopeSlug(scope)}-${timestampSlug()}.csv`,
  );
}
